import { useEffect } from 'react';
import { X, Users, MessageSquare, Activity, AlertTriangle, Gauge, Brain } from 'lucide-react';
import { useAdminStore, type AdminTab } from '@/stores/adminStore';
import { useAdminPanel } from '@/hooks/useAdminPanel';
import ActiveUsersTab from './tabs/ActiveUsersTab';
import QueryFeedTab from './tabs/QueryFeedTab';
import SystemHealthTab from './tabs/SystemHealthTab';
import ErrorLogTab from './tabs/ErrorLogTab';
import PerformanceTab from './tabs/PerformanceTab';
import LearningSystemTab from './tabs/LearningSystemTab';

const TABS: { id: AdminTab; label: string; icon: typeof Users }[] = [
  { id: 'users', label: 'Usuarios', icon: Users },
  { id: 'queries', label: 'Consultas', icon: MessageSquare },
  { id: 'health', label: 'Sistema', icon: Activity },
  { id: 'learning', label: 'Aprendizaje', icon: Brain },
  { id: 'errors', label: 'Errores', icon: AlertTriangle },
  { id: 'performance', label: 'Rendimiento', icon: Gauge },
];

export default function AdminPanel() {
  const isOpen = useAdminStore((s) => s.isOpen);
  const activeTab = useAdminStore((s) => s.activeTab);
  const setOpen = useAdminStore((s) => s.setOpen);
  const setActiveTab = useAdminStore((s) => s.setActiveTab);
  const lastUpdated = useAdminStore((s) => s.lastUpdated);
  const errorCount = useAdminStore((s) => s.errorLog?.length ?? 0);

  // Start polling when open
  useAdminPanel();

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    if (isOpen) document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, setOpen]);

  if (!isOpen) return null;

  const TabContent = {
    users: ActiveUsersTab,
    queries: QueryFeedTab,
    health: SystemHealthTab,
    learning: LearningSystemTab,
    errors: ErrorLogTab,
    performance: PerformanceTab,
  }[activeTab];

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 z-[1100] transition-opacity"
        onClick={() => setOpen(false)}
      />

      {/* Panel */}
      <div className="fixed top-0 right-0 bottom-0 w-[540px] max-w-[90vw] bg-bg-secondary border-l border-border-strong z-[1101] flex flex-col animate-slide-in-right shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border-subtle">
          <div>
            <h2 className="text-base font-bold text-text-primary">Monitor</h2>
            {lastUpdated && (
              <span className="text-[10px] text-text-subtle">
                {new Date(lastUpdated).toLocaleTimeString()}
              </span>
            )}
          </div>
          <button
            onClick={() => setOpen(false)}
            className="w-8 h-8 rounded-lg bg-bg-tertiary border border-border-subtle text-text-muted hover:text-text-primary hover:border-accent-blue flex items-center justify-center transition-all"
          >
            <X size={16} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border-subtle px-2 gap-1 overflow-x-auto">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium border-b-2 transition-all whitespace-nowrap ${
                  isActive
                    ? 'border-accent-blue text-accent-blue'
                    : 'border-transparent text-text-muted hover:text-text-secondary'
                }`}
              >
                <Icon size={14} />
                {tab.label}
                {tab.id === 'errors' && errorCount > 0 && (
                  <span className="ml-1 bg-accent-red text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                    {errorCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto p-4">
          <TabContent />
        </div>
      </div>
    </>
  );
}
